import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  PopoverOverlay,
  FormContainer,
  FormHeader,
  CloseButton,
  FormBody,
  InputGroup,
  InputGrid,
  SubmitButton,
  SuccessOverlay,
} from './CatalogueForm.styled';

const CatalogueForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // 'idle', 'submitting', 'success'

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `City: ${formData.city}\n\n${formData.message}`,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');

        // Trigger download
        const link = document.createElement('a');
        link.href = '/pdf/Swan company profile 2025 (2).pdf';
        link.download = 'Swan company profile 2025.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Reset after success view
        setTimeout(() => {
          setStatus('idle');
          onClose();
          setFormData({
            name: '',
            email: '',
            phone: '',
            city: '',
            message: '',
          });
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert(error.message || 'Failed to send message. Please try again.');
      setStatus('idle');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <PopoverOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <FormContainer
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 100, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <FormHeader>
              <h3>Exclusive Access to Swan Catalogue</h3>
              <p>
                Please provide your details to download our complete product
                profile for 2025.
              </p>
              <CloseButton onClick={onClose}>×</CloseButton>
            </FormHeader>

            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <SuccessOverlay
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <h4>Download Started!</h4>
                  <p>
                    Thank you for your interest in Swan Pipe Support. We will
                    get in touch with you soon.
                  </p>
                </SuccessOverlay>
              ) : (
                <FormBody key="form" onSubmit={handleSubmit}>
                  <InputGroup>
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your name"
                    />
                  </InputGroup>

                  <InputGrid>
                    <InputGroup>
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </InputGroup>
                    <InputGroup>
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="+91 00000 00000"
                      />
                    </InputGroup>
                  </InputGrid>

                  <InputGroup>
                    <label htmlFor="city">City / Region *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. Madurai, Tamil Nadu"
                    />
                  </InputGroup>

                  <InputGroup>
                    <label htmlFor="message">How can we help? *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="3"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Your message..."
                    />
                  </InputGroup>

                  <SubmitButton
                    type="submit"
                    disabled={status === 'submitting'}
                  >
                    {status === 'submitting' ? (
                      'Preparing Your File...'
                    ) : (
                      <>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download Catalogue
                      </>
                    )}
                  </SubmitButton>
                </FormBody>
              )}
            </AnimatePresence>
          </FormContainer>
        </PopoverOverlay>
      )}
    </AnimatePresence>
  );
};

export default CatalogueForm;
